import {
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import getDocumentDuplicates from 'api/v2/repository/getDocumentDuplicates';
import AIIcon from 'assets/svg/icons/AIIcon';
import { IconButton } from 'components/atoms';
import AlertV2 from 'components/atoms/AlertV2/AlertV2.component';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import AIAssistantSummary from 'components/molecules/AIAssistantSummary.component';
import SlideOver2 from 'components/molecules/SlideOver2.component';
import { DocumentDuplicateFinder } from 'components/organisms/DocumentDuplicateFinder';
import { useRepositoryDocumentAiMetadata } from 'contexts/AIMetadataProvider/useRepositoryDocumentAiMetadata';
import { joiResolver } from 'customJoi';
import dayjs from 'dayjs';
import { AnimatePresence } from 'framer-motion';
import useDeleteTag from 'hooks/DataExport/useDeleteTag';
import useCreateTag from 'hooks/DocumentsRepository/useCreateTag';
import useDeleteDocument from 'hooks/DocumentsRepository/useDeleteDocument';
import useGetRepositoryTags from 'hooks/DocumentsRepository/useGetRepositoryTags';
import useUpdateDocument from 'hooks/DocumentsRepository/useUpdateDocument';
import useUpdateTag from 'hooks/DocumentsRepository/useUpdateTag';
import getIntercom from 'hooks/intercom.hook';
import useConfirm from 'hooks/useConfirm';
import useFeatures from 'hooks/useFeatures';
import {
  naturalClose,
  useFormSlideOverManager,
  type FormSlideOverManagerOptions,
} from 'hooks/useFormSlideOverManager';
import { HighlightProvider } from 'hooks/useHighlight';
import Joi from 'joi';
import { useCallback, useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { components } from 'react-select';
import { AlertIdsEnum } from 'types/AlertIdsEnum';
import type { DocumentItem, TagItemType } from 'types/repository.types';
import { ONE_SECOND } from 'utils/time';
import showToast from 'utils/toast';
import { formatErrorMessage } from 'validation/commonValidators';
import FileViewer from '../Expenses/Form/FlieViewer';
import DocumentRepositoryFields from './DocumentReposioryFields';
import DocumentRepositoryTaxInfo from './DocumentRepositoryTaxInfo';
import useDocumentFields from './useDocumentFields';

const formatSuggestedTags = (
  guessedTags: TagItemType[],
  tagsValue: string[],
  dismissedSuggestedTags: string[],
  document: DocumentItem,
) => {
  if (document?.tags?.length) return [];

  return guessedTags
    ?.filter((tag) => !tagsValue.includes(tag._id))
    ?.filter((tag) => !dismissedSuggestedTags.includes(tag._id));
};

const TagOption: FC<any> = ({
  children,
  onDelete,
  onEdit,
  isGuessed,
  ...rest
}) => {
  return (
    <components.Option
      {...rest}
      className="flex items-center justify-between w-full"
    >
      <div className="flex items-center justify-between w-full group">
        <div className="text-sm font-semibold text-primary flex items-center gap-2">
          {children}

          {isGuessed ? (
            <span className="text-xs text-taxes ai-border-animated rounded-full pl-1 pr-2 flex items-center gap-1">
              <AIIcon className="w-5 h-5" />
              <span>Our guess</span>
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <IconButton
            className="bg-transparent !w-6 !h-6"
            icon={() => (
              <PencilIcon className="w-5 h-5 text-primary-400 group-hover:text-primary transition-colors" />
            )}
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(rest.data);
            }}
          />

          <IconButton
            className="bg-transparent !w-6 !h-6"
            icon={() => (
              <TrashIcon className="w-5 h-5 text-primary-400 group-hover:text-primary transition-colors" />
            )}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(rest.data);
            }}
          />
        </div>
      </div>
    </components.Option>
  );
};

const SelectMenuButton: FC<any> = (props) => {
  const { t } = useTranslation();
  return (
    <components.MenuList {...props} className="relative">
      {props.children}

      <Button
        asDefault
        className="flex items-center justify-center gap-2 p-2 sticky bottom-0 bg-white w-full shadow-topShadow py-3 hover:bg-primary-50"
        onClick={props.onAddTag}
      >
        <PlusCircleIcon className="w-4 h-4 text-primary" />

        <span>{t('repository.document_form.custom_tag.label')}</span>
      </Button>
    </components.MenuList>
  );
};

const FormWrapper: FC<{
  document: DocumentItem;
  onClose: () => void;
  isFormDirtyRef: any;
  afterSubmit: () => void;
  afterDelete: () => void;
  openDocument: (doc: DocumentItem) => void;
}> = ({
  document,
  onClose,
  isFormDirtyRef,
  afterSubmit,
  afterDelete,
  openDocument,
}) => {
  const { t } = useTranslation();

  const dateFormat = useFeatures('settings.dateFormat');

  const { showIntercom } = getIntercom();

  const [dismissedSuggestedTags, setDismissedSuggestedTags] = useState<
    string[]
  >([]);

  const formAPI = useForm({
    defaultValues: {
      name: document?.file?.name,
      tags: document?.tags?.map(({ _id }: TagItemType) => _id),
      dateNumber: dayjs(String(document?.dateNumber), 'YYYYMMDD').toDate(),
      tagName: '',
    },
    resolver: joiResolver(
      Joi.object({
        name: Joi.string()
          .required()
          .messages(formatErrorMessage('repository', 'name')),
      }),
      { allowUnknown: true },
    ),
  });

  const tags = useGetRepositoryTags();

  const updateDocumentAPI = useUpdateDocument();

  const [confirmDeleteDocumentContent, confirmDeleteDocumentAPI] = useConfirm();

  const [confirmDeleteTagContent, confirmDeleteTagAPI] = useConfirm();

  const deleteDocumentAPI = useDeleteDocument();

  const [toggleTagForm, setToggleTagForm] = useState(false);

  const [editableTag, setEditableTag] = useState<TagItemType | null>(null);

  const deleteTagAPI = useDeleteTag();

  const { data: documentFields } = useDocumentFields(document?._id);

  const addTagAPI = useCreateTag((newTag) => {
    formAPI.setValue('tags', [...formAPI.getValues('tags'), newTag._id]);
    formAPI.setValue('tagName', '');
    setToggleTagForm(false);
    showToast(
      { title: t('repository.tags.add_tag.success_msg') },
      { type: 'success' },
    );
  });

  const updateTagAPI = useUpdateTag(() => {
    formAPI.setValue('tagName', '');
    setToggleTagForm(false);
    showToast(
      { title: t('repository.tags.edit_tag.success_msg') },
      { type: 'success' },
    );
  });

  // const isDirty = !_isEmpty(formAPI.formState.dirtyFields);

  // useImperativeHandle(isFormDirtyRef, () => isDirty, [isDirty]);

  const onDeleteDocument = (documentId: string) =>
    confirmDeleteDocumentAPI.open({
      okText: t('confirm'),
      color: 'primary',
      message: t('repository.document_form.delete_doc.title'),
      onConfirm: () => {
        return deleteDocumentAPI.mutateAsync(documentId).then(afterDelete);
      },
      className: 'text-center font-semibold',
      buttonsWrapperClassName: 'justify-center',
    });

  const onDeleteTag = (tagId: string) =>
    confirmDeleteTagAPI.open({
      okText: t('confirm'),
      color: 'primary',
      message: t('repository.document_form.delete_tag.title'),
      onConfirm: () => {
        return deleteTagAPI.mutateAsync(tagId);
      },
      className: 'text-center font-semibold',
      buttonsWrapperClassName: 'justify-center',
    });

  const tagsValue = formAPI.watch('tags');

  const suggestedTags = formatSuggestedTags(
    document?.guessedTags,
    tagsValue,
    dismissedSuggestedTags,
    document,
  );

  const suggestedTagSet = useMemo(
    () => new Set(document?.guessedTags?.map(({ _id }) => _id)),
    [document?.guessedTags],
  );

  const onSubmitTagForm = useCallback(() => {
    if (editableTag && editableTag._id) {
      return updateTagAPI.mutate({
        tagId: editableTag._id,
        tagName: formAPI.watch('tagName'),
      });
    }

    return addTagAPI.mutate(formAPI.watch('tagName'));
  }, [editableTag, formAPI, addTagAPI, updateTagAPI]);

  return (
    <div className="h-full">
      {confirmDeleteDocumentContent}

      {confirmDeleteTagContent}

      <FormProvider {...formAPI}>
        <form
          className="flex flex-col h-full overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            formAPI.handleSubmit((values) => {
              return updateDocumentAPI.mutate(
                {
                  documentId: document?._id,
                  name: values.name,
                  dateNumber:
                    Number(dayjs(values.dateNumber).format('YYYYMMDD')) ||
                    undefined,
                  tags: values.tags || [],
                },
                { onSuccess: () => afterSubmit?.() },
              );
            })();
          }}
        >
          <div className="bg-white flex-1 overflow-y-auto">
            <div className="p-6 flex flex-col gap-5">
              {/* Document Name */}
              <FieldConnector name="name">
                {({ field }) => (
                  <FreeTextInput
                    {...field}
                    innerClassName="!bg-transparent !border-none !shadow-none !ring-0"
                    className="!p-0 !px-0 !py-0 !font-avenir !text-primary focus:!bg-transparent hover:!bg-transparent !font-bold !tracking-[-.045em] !text-2xl !text-ellipsis !bg-transparent !border-none !shadow-none"
                  />
                )}
              </FieldConnector>

              {/* Document Duplicate Finder */}
              <DocumentDuplicateFinder
                className="mb-10"
                onFetch={() => {
                  return getDocumentDuplicates(document?._id) as any;
                }}
                renderLabel={(duplicate) => `${duplicate.file.name}`}
                onSelect={(_, doc) => openDocument(doc as any)}
                onDelete={(dup) =>
                  deleteDocumentAPI.mutateAsync(dup._id) as any
                }
              />

              {/* Invoice / Expense Warning */}
              {['expense', 'revenue'].includes(document.documentType) ? (
                <AlertV2
                  variant="filled"
                  type="warning"
                  size="small"
                  title={t('repository.document_form.fields.invoice.title')}
                  description={
                    <ReactMarkdown>
                      {t('repository.document_form.fields.invoice.description')}
                    </ReactMarkdown>
                  }
                  dismissible
                  dismissId={AlertIdsEnum.repositoryDocumentFormInvoice}
                />
              ) : null}

              {/* Tax Info Alert */}
              <DocumentRepositoryTaxInfo documentId={document?._id} />

              {/* Document Date */}
              <FieldConnector name="dateNumber" label={t('date')}>
                {({ field }) => (
                  <DatePicker
                    selected={field.value || null}
                    scrollableYearDropdown
                    showYearDropdown
                    onChange={(value) => field.onChange(value)}
                    placeholder={dateFormat}
                  />
                )}
              </FieldConnector>

              {/* Tags */}
              <FieldConnector
                name="tags"
                label={t('documents_repository.filters.tags')}
              >
                {({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    isLoading={tags.isLoading}
                    options={tags.data?.map((tag) => ({
                      label: tag.name,
                      value: tag._id,
                      ...tag,
                    }))}
                    onChange={(selected) => {
                      field.onChange(
                        selected.map(({ value }: { value: string }) => value),
                      );
                    }}
                    placeholder=""
                    size="xs"
                    themeOptions={{
                      controlClassName: 'pr-2.5 !min-h-[44px]',
                      optionClassName: '!py-2.5 !px-3',
                    }}
                    components={{
                      MultiValueContainer: ({ children, data }) => (
                        <div
                          className="bg-white rounded-full p-1 flex items-center gap-2 py-1 pl-2.5 pr-1.5 mx-1 font-semibold text-sm my-0.5"
                          style={{
                            backgroundColor: `${data.color}17`,
                            color: data.color,
                          }}
                        >
                          {children}
                        </div>
                      ),
                      Option: (props) => (
                        <TagOption
                          {...props}
                          isGuessed={suggestedTagSet.has(props.data._id)}
                          onDelete={(tagOption: TagItemType) =>
                            onDeleteTag(tagOption._id)
                          }
                          onEdit={(tagOption: TagItemType) => {
                            setEditableTag(tagOption);

                            formAPI.setValue('tagName', tagOption.name);

                            setToggleTagForm(true);
                          }}
                        />
                      ),
                      MenuList: (props) => (
                        <SelectMenuButton
                          {...props}
                          onAddTag={() => {
                            setToggleTagForm(true);

                            formAPI.setValue('tagName', '');

                            setEditableTag(null);
                          }}
                        />
                      ),
                    }}
                  />
                )}
              </FieldConnector>

              {/* Tag Form */}
              <FieldConnector name="tagName" showField={toggleTagForm}>
                {({ field }) => (
                  <div className="relative mt-4">
                    <FreeTextInput
                      {...field}
                      placeholder={t(
                        'repository.tag_form.tag_name.placeholder',
                      )}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();

                          onSubmitTagForm();
                        }
                      }}
                    />

                    <div className="absolute top-0 right-3 bottom-0 flex items-center justify-center">
                      <Button
                        loading={addTagAPI.isLoading || updateTagAPI.isLoading}
                        disabled={
                          !formAPI.watch('tagName') ||
                          addTagAPI.isLoading ||
                          updateTagAPI.isLoading
                        }
                        onClick={onSubmitTagForm}
                        className="z-[5]"
                      >
                        {editableTag && editableTag._id
                          ? t('settings.update_cta')
                          : t('add')}
                      </Button>
                    </div>
                  </div>
                )}
              </FieldConnector>

              {/* User Fields Suggestions */}
              <DocumentRepositoryFields documentId={document?._id} />

              <AIAssistantSummary
                message={document?.AISummary}
                showAskAIAssistant={true}
              />

              <div className="mt-2">
                <Button
                  structure="text"
                  color="red"
                  loading={deleteDocumentAPI.isLoading}
                  onClick={() => onDeleteDocument(document?._id)}
                  className="text-[#DC2626]"
                >
                  <TrashIcon className="w-5 mr-2" />
                  <span className="font-semibold">{t('delete')}</span>
                </Button>
              </div>

              <div className="border-t border-primary-100 pt-5 mt-8">
                <p className="text-right text-primary-400 text-sm leading-tight">
                  {t('repository.document_form.upload_time_msg', {
                    time: dayjs(document?.created).format('ll'),
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-primary-100 pt-4 pb-2 px-6 bg-white">
            <Button
              className="w-full mb-3 text-sm py-2.5"
              loading={updateDocumentAPI.isLoading}
              onClick={() => onClose()}
              structure="secondary"
            >
              {t('close')}
            </Button>

            <Button
              className="w-full mb-3 text-sm py-2.5"
              type="submit"
              loading={updateDocumentAPI.isLoading}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const FormSlideOver: FC<
  FormSlideOverManagerOptions<any> & {
    afterSubmit?: () => void;
  }
> = ({ apiRef, onClose, onOpen, afterSubmit }) => {
  const { doc, api, isFormDirtyRef, confirmContent, options, formKey } =
    useFormSlideOverManager<DocumentItem, Partial<any>>({
      enableNavigationBlockingIfDirty: true,
      apiRef: apiRef,
      onClose,
      onOpen,
    });

  useRepositoryDocumentAiMetadata(doc || undefined);

  return (
    <AnimatePresence mode="wait">
      {!!doc && (
        <HighlightProvider
          key={formKey}
          unHighlightTimer={2 * ONE_SECOND}
          className="animate-taxesPulse rounded-lg"
        >
          <SlideOver2
            showLeftPanelUI={true}
            onClose={() => api.close(naturalClose)}
            renderLeftPanel={() => {
              if (!doc?.file?.path) return null;
              return (
                <FileViewer
                  floatingItemsMargin="var(--rightPaneWidth)"
                  filePath={doc?.file?.path}
                  disabled
                />
              );
            }}
            renderRightPanel={() => {
              return (
                <FormWrapper
                  document={doc}
                  onClose={() => api.close(naturalClose)}
                  isFormDirtyRef={isFormDirtyRef}
                  afterSubmit={() => {
                    api.close(naturalClose);
                    afterSubmit?.();
                  }}
                  afterDelete={api.close}
                  openDocument={api.open}
                />
              );
            }}
          />
        </HighlightProvider>
      )}
    </AnimatePresence>
  );
};

export default FormSlideOver;
